import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS, stateOptions } from '../commonStateTaxData';
import { step1Questions, completeFormInitialQuestion, reciprocalStateOptions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function PennsylvaniaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formatFullName,
  markActionAsCompleted,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  const completeForm = useRef();
  const completeFormQuestionWithAnswers = getInitialQuestions(completeFormInitialQuestion, 'no_need');
  const [completeFormQuestion, setCompleteForm] = useState(completeFormQuestionWithAnswers);

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const [exemption, setExemption] = useState(data.exemption || '');
  const [exemptionError, setExemptionError] = useState(false);
  const [reciprocalState, setReciprocalState] = useState(data.reciprocal_state || '');
  const [reciprocalStateError, setReciprocalStateError] = useState(false);
  const [legalResidentState, setLegalResidentState] = useState(data.legal_resident_state || '');
  const [legalResidentStateError, setLegalResidentStateError] = useState(false);

  const needToCompleteForm = () => (completeFormQuestion[0].answer.answer.is_exempt.includes('need_to_complete'));

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
    </div>
  );

  const renderCompleteFormQuestion = (isReviewStep) => (
    <Flex column>
      <Flex mt="10px" mb="15px">
        <Text>
          Pennsylvania does not have a form exactly like the federal W-4 form,
          since Pennsylvania Personal Income Tax is based on a flat tax rate and
          everyone pays the same rate of 3.07 percent.  Pennsylvania does not have
          allowances based on the number of dependents claimed, marital status,
          age, etc. However, you may be entitled to non withholding of PA personal
          income tax if you incurred no liability for income tax the preceding tax
          year and/or you anticipate that you will incur no liability for income tax
          during the current tax year, according to the Special Tax Provisions of
          section 304 of the Tax Reform Code, the Service member Civil Relief Act
          (SCRA) or as a resident of the reciprocal state of Indiana, Maryland,
          New Jersey, Ohio, Virginia or West Virginia and your employer agrees to
          withhold the income tax from that state.  If you meet these criteria you
          will need to complete form REV-419.
        </Text>
      </Flex>
      <FormConsumer
        questions={completeFormQuestion}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = completeFormQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setCompleteForm(questions);
        }}
        ref={completeForm}
      />
    </Flex>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column mb="20px">
      <Text bold fontSize="14px" mb="12px">
        Exemptions from withholding
      </Text>
      <S.QuestionWrapper>
        <Flex mb="7px" column>
          <S.QuestionLabel isRequired={needToCompleteForm()}>
            If you are claiming exemption from withholding, choose one of the following
            options that describes you.
          </S.QuestionLabel>
        </Flex>
        <Flex column mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '22px' }}
                type="radio"
                value="tax_forgiveness"
                checked={exemption === 'tax_forgiveness'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemption(e.target.value);
                }}
              />
              <Box ml={2} />
              <Text>
                I qualified for Tax Forgiveness of my PA personal income tax liability
                last year, and had a right to a full refund of all income tax withheld
                and/or I expect to qualify for Tax Forgiveness of my PA personal income
                tax liability this year and expect to have a right to a full refund of
                all income tax withheld.
              </Text>
            </Flex>
          </label>
        </Flex>
        <Flex column mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '22px' }}
                type="radio"
                value="resident_of_reciprocal_state"
                checked={exemption === 'resident_of_reciprocal_state'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemption(e.target.value);
                }}
              />
              <Box ml={2} />
              <Text>
                I declare I am a resident of a reciprocal state and that pursuant
                to the reciprocal tax agreement between that state and PA, I claim
                an exemption from withholding of PA personal income tax and authorize
                my employer to withhold income tax for my resident state on compensation
                paid to me in the Commonwealth of Pennsylvania.
              </Text>
            </Flex>
          </label>
          {exemption === 'resident_of_reciprocal_state' && (
          <Flex column mt="8px" mb="8px">
            <S.QuestionWrapper>
              <Flex mb="7px" column>
                <S.QuestionLabel isRequired>
                  I am a resident of:
                </S.QuestionLabel>
              </Flex>
              <S.Select
                disabled={isReviewStep}
                value={reciprocalState}
                onChange={(e) => setReciprocalState(e.target.value)}
              >
                <option value="" style={{ display: 'none', color: '#666' }} key={0}>
                  Select from dropdown
                </option>
                {reciprocalStateOptions.map((option, idx) => (
                  <option value={option.codeValue} key={idx}>
                    {option.shortName}
                  </option>
                ))}
              </S.Select>
              {reciprocalStateError && (
              <Flex justifyContent="flex-start">
                <Text color="red">This is a mandatory field.</Text>
              </Flex>
              )}
            </S.QuestionWrapper>
          </Flex>
          )}
        </Flex>
        <Flex column mb="8px">
          <label>
            <Flex>
              <S.Input
                style={{ height: '22px' }}
                type="radio"
                value="legal_resident_of_state"
                checked={exemption === 'legal_resident_of_state'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemption(e.target.value);
                }}
              />
              <Box ml={2} />
              <Text>
                I certify I am not subject to Pennsylvania withholding because I
                meet the requirements set forth under the Servicemembers Civil Relief
                Act, as amended, and as set forth in revised Personal Income Tax
                Bulletin 2010-01.
              </Text>
            </Flex>
          </label>
        </Flex>
        {exemption === 'legal_resident_of_state' && (
        <Flex column>
          <S.QuestionWrapper>
            <Flex mb="7px">
              <S.QuestionLabel isRequired>
                I am a legal resident of:
              </S.QuestionLabel>
            </Flex>
            <S.Select
              disabled={isReviewStep}
              value={legalResidentState}
              onChange={(e) => setLegalResidentState(e.target.value)}
            >
              <option value="" style={{ display: 'none', color: '#666' }} key={0}>
                Select from dropdown
              </option>
              {stateOptions.map((option, idx) => (
                <option value={option.codeValue} key={idx}>
                  {option.shortName}
                </option>
              ))}
            </S.Select>
            {legalResidentStateError && (
            <Flex justifyContent="flex-start">
              <Text color="red">This is a mandatory field.</Text>
            </Flex>
            )}
          </S.QuestionWrapper>
        </Flex>
        )}
        {exemptionError && (
        <Flex justifyContent="flex-start">
          <Text color="red">This is a mandatory field.</Text>
        </Flex>
        )}
      </S.QuestionWrapper>
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderCompleteFormQuestion()}
          {needToCompleteForm() && renderStep1Form()}
          {needToCompleteForm() && renderExemptionsFromWithholding()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && needToCompleteForm() && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (needToCompleteForm()) {
              if (exemption === '') {
                err = true;
                setExemptionError(true);
              } else {
                setExemptionError(false);
              }
              if (exemption === 'resident_of_reciprocal_state') {
                if (reciprocalState === '') {
                  err = true;
                  setReciprocalStateError(true);
                } else {
                  err = false;
                }
              }
              if (exemption === 'legal_resident_of_state') {
                if (legalResidentState === '') {
                  err = true;
                  setLegalResidentStateError(true);
                } else {
                  err = false;
                }
              }
              if (step1Form && step1Form.current) {
                step1Form.current.validate((error) => {
                  if (error) err = error;
                });
              }
              if (completeForm && completeForm.current) {
                completeForm.current.validate((error) => {
                  if (error) err = error;
                });
              }
            }
            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step
              setExemptionError(false);
              setReciprocalStateError(false);
              setLegalResidentStateError(false);
              let stepOneData = resetInitialQuestions(step1Questions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              let isExemptTemp = 'no_need';
              let exemptionTemp = '';
              let reciprocalStateTemp = '';
              let legalResidentStateTemp = '';
              if (needToCompleteForm()) {
                stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                isExemptTemp = 'need_to_complete';
                exemptionTemp = exemption;
                if (exemption === 'resident_of_reciprocal_state') reciprocalStateTemp = reciprocalState;
                if (exemption === 'legal_resident_of_state') legalResidentStateTemp = legalResidentState;
              }
              const exemptionData = {
                is_exempt: isExemptTemp,
                exemption: exemptionTemp,
                reciprocal_state: reciprocalStateTemp,
                legal_resident_state: legalResidentStateTemp,
              };
              const data = { ...stepOneData, ...exemptionData };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderCompleteFormQuestion(true)}
        {needToCompleteForm() && renderStep1Form(true)}
        {needToCompleteForm() && renderExemptionsFromWithholding(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              if (needToCompleteForm()) {
                handleSigning();
                setCurrentStep(STEPS.step3);
                scrollTop();
              } else {
                markActionAsCompleted();
              }
            }}
            loading={isSubmitting}
          >
            {needToCompleteForm() ? 'Finalize and sign' : 'Done'}
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default PennsylvaniaForm;
